import React, { useEffect, useRef, useState } from "react";
import "style/LangMenu.scss";

import { shortCode } from "config/i18n/languages";

export interface MenuLang {
  code: string;
  label: string;
}

interface Props {
  /** Languages offered (the site's, or all 23 on the JAMA page). */
  langs: readonly MenuLang[];
  current: string;
  /** "Language" in the current language (screen-reader label). */
  label: string;
  onSelect: (lang: string) => void;
}

/**
 * Language menu of the navbar: a pill (globe + code) that opens a small
 * panel with every language in its own name. Closes on selection, outside
 * click and Escape; arrow keys, Home and End move between the entries.
 */
const LangMenu = ({ langs, current, label: menuLabel, onSelect }: Props) => {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent | TouchEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer, { passive: true });
    document.addEventListener("keydown", onKey);
    // Focus the active language when the panel opens.
    const index = langs.findIndex((item) => item.code === current);
    items.current[index]?.focus();
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, current, langs]);

  const onListKey = (event: React.KeyboardEvent) => {
    const list = items.current.filter(Boolean) as HTMLButtonElement[];
    const index = list.indexOf(document.activeElement as HTMLButtonElement);
    const move = (to: number) => {
      event.preventDefault();
      list[(to + list.length) % list.length]?.focus();
    };
    if (event.key === "ArrowDown") move(index + 1);
    else if (event.key === "ArrowUp") move(index - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(list.length - 1);
    else if (event.key === "Tab") setOpen(false);
  };

  const label =
    langs.find((item) => item.code === current)?.label || "Language";

  return (
    <div className={`langmenu${open ? " is-open" : ""}`} ref={root}>
      <button
        ref={button}
        type="button"
        className="langmenu__button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${menuLabel}: ${label}`}
        onClick={() => setOpen(!open)}
      >
        <svg
          className="langmenu__globe"
          viewBox="0 0 24 24"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
        </svg>
        <span className="langmenu__code">
          {shortCode(current).toUpperCase()}
        </span>
        <span className="langmenu__chevron" aria-hidden="true" />
      </button>

      <div
        className={`langmenu__panel${
          langs.length > 12 ? " langmenu__panel--grid" : ""
        }`}
        hidden={!open}
        onKeyDown={onListKey}
      >
        <ul role="list">
          {langs.map((item, index) => {
            const active = item.code === current;
            return (
              <li key={item.code}>
                <button
                  ref={(el) => (items.current[index] = el)}
                  type="button"
                  lang={item.code}
                  className={`langmenu__item${active ? " is-active" : ""}`}
                  aria-current={active ? "true" : undefined}
                  onClick={() => {
                    setOpen(false);
                    if (!active) onSelect(item.code);
                  }}
                >
                  <span className="langmenu__name">{item.label}</span>
                  <span className="langmenu__tag">
                    {active ? (
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path
                          d="M3.5 8.4l2.9 2.9 6.1-6.6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      shortCode(item.code).toUpperCase()
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default LangMenu;
